import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../Navigation/navigation';
import { useAuth } from '../Auth/AuthContext';
import api from '../../utils/api';

const Dashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalStaff: 0,
    myStaff: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch staff data using single API endpoint
      const staffResponse = await api.get('/api/staff');
      const staffData = staffResponse.data;
      
      // For regular users, this will be their own data
      // For admin, this will be all staff data
      const totalStaffCount = staffData.length;
      const myStaffCount = user?.role === 'admin' ? 
        staffData.filter(staff => staff.created_by === user.user_id).length : 
        totalStaffCount;

      setStats({
        totalStaff: totalStaffCount,
        myStaff: myStaffCount,
        recentActivity: staffData.slice(0, 5) // Last 5 records
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.role, user?.user_id]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
    }
  }, [isAuthenticated, fetchDashboardData]);

  const quickActions = [
    {
      title: 'Add New Staff',
      description: 'Create a new staff record with comprehensive information',
      action: () => navigate('/comprehensive-staff-wizard'),
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      title: 'Edit Staff Data',
      description: 'Search and edit existing staff information',
      action: () => navigate('/staff-management'),
       color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      title: 'Export Data',
      description: 'Export staff data in various formats',
      action: () => navigate('/data-export'),
      color: 'bg-blue-500 hover:bg-blue-600'
    }
  ];

  // Add admin-only actions
  if (user?.role === 'admin') {
    quickActions.push(
      {
        title: 'View All Staff',
        description: 'See complete staff list with management options',
        action: () => navigate('/staff-management'),
        color: 'bg-orange-500 hover:bg-orange-600'
      },
      {
        title: 'User Management',
        description: 'Manage user accounts and permissions',
        action: () => navigate('/user-management'),
        color: 'bg-red-500 hover:bg-red-600'
      }
    );
  }

  if (!isAuthenticated) {
    return <div>Please log in to access the dashboard.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-7xl mx-auto p-5 pt-20">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-700">
                  {user?.role === 'admin' ? 'Total Staff' : 'My Staff Records'}
                </h3>
                <p className="text-3xl font-bold text-blue-600">
                  {loading ? '...' : (user?.role === 'admin' ? stats.totalStaff : stats.myStaff)}
                </p>
              </div>
            </div>
          </div>

          {user?.role === 'admin' && (
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-full">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-700">System Status</h3>
                  <p className="text-xl font-bold text-green-600">Active</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-full">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-700">Last Login</h3>
                <p className="text-sm font-medium text-purple-600">
                  {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quickActions.map((action, index) => (
              <div
                key={index}
                onClick={action.action}
                className={`${action.color} text-white p-6 rounded-xl shadow-lg cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-xl`}
              >
                <h3 className="text-xl font-bold mb-2">{action.title}</h3>
                <p className="text-sm opacity-90">{action.description}</p>
                <div className="mt-4 flex justify-end">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Recent Staff Records</h2>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading recent activity...</p>
            </div>
          ) : stats.recentActivity.length > 0 ? (
            <div className="space-y-4">
              {stats.recentActivity.map((staff, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                      {staff.fullname?.charAt(0)?.toUpperCase() || 'N'}
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-800">{staff.fullname || 'No Name'}</h4>
                      <p className="text-sm text-gray-600">ID: {staff.staff_id}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      {new Date(staff.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Staff Records Yet</h3>
              <p className="text-gray-600 mb-4">Start by adding your first staff member</p>
              <button
                onClick={() => navigate('/comprehensive-staff-wizard')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Add First Staff Member
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
